import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useRef, useEffect, useState } from 'react';
import { SalesNotification } from '@/store/videoFeatures';
import { useSalesNotification } from '@/hooks/use-sales-notification';
import { cn } from '@/lib/utils';
import { PLATFORM_ICONS, PLATFORM_DISPLAY_NAMES } from '@/lib/sales-notification-platforms';
// As classes animate-slide-in-up / zoom-in / bounce-in / *-out vivem SÓ neste
// arquivo, e o barrel ui/index.ts que o importava não é usado por ninguém —
// sem este import as animações de entrada e saída ficam mudas, sem erro.
import './animations.css';
import { normalizeRedirectUrl } from '@/lib/redirect-url';


interface SalesNotificationOverlayProps {
  salesNotification: SalesNotification;
  forceShow?: boolean;
}

export const SalesNotificationOverlay: React.FC<SalesNotificationOverlayProps> = ({ 
  salesNotification,
  forceShow = false
}) => {
  useLanguage();
  const { visible, exiting, currentMessage } = useSalesNotification({ 
    salesNotification, 
    forceShow 
  });

  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Track if entry animation has completed
  const [hasEntered, setHasEntered] = useState(false);
  const prevVisibleRef = useRef(false);
  
  // Reset hasEntered when visibility changes
  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      // Just became visible - allow entry animation
      setHasEntered(false);
      // Mark as entered after animation completes
      const timer = setTimeout(() => setHasEntered(true), 350);
      return () => clearTimeout(timer);
    }
    if (!visible) {
      setHasEntered(false);
    }
    prevVisibleRef.current = visible;
  }, [visible]);
  
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = (salesNotification.soundVolume || 50) / 100;
    }
  }, [salesNotification.soundVolume]);

  useEffect(() => {
    if (visible && !prevVisibleRef.current && salesNotification.soundEnabled) {
      if (audioRef.current) {
        audioRef.current.volume = (salesNotification.soundVolume || 50) / 100;
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(error => {
          console.log("Audio play failed:", error);
        });
      }
    }
  }, [visible, salesNotification.soundEnabled, salesNotification.soundVolume]);
  
  if (!visible && !forceShow) return null;
  
  // Determine platform icon to use
  const getPlatformIcon = () => {
    if (salesNotification.platform === 'custom') {
      return salesNotification.customPlatformIcon;
    }
    return PLATFORM_ICONS[salesNotification.platform as keyof typeof PLATFORM_ICONS];
  };
  
  // Determine position classes - use fixed positioning, no conflicting transforms
  const getPositionStyles = (): React.CSSProperties => {
    const base: React.CSSProperties = { position: 'absolute' };
    switch (salesNotification.position) {
      case 'top-left':
        return { ...base, top: 16, left: 16 };
      case 'top-center':
        return { ...base, top: 16, left: '50%', transform: 'translateX(-50%)' };
      case 'top-right':
        return { ...base, top: 16, right: 16 };
      case 'middle-left':
        return { ...base, top: '50%', left: 16, transform: 'translateY(-50%)' };
      case 'middle-center':
        return { ...base, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
      case 'middle-right':
        return { ...base, top: '50%', right: 16, transform: 'translateY(-50%)' };
      case 'bottom-left':
        return { ...base, bottom: 16, left: 16 };
      case 'bottom-center':
        return { ...base, bottom: 16, left: '50%', transform: 'translateX(-50%)' };
      case 'bottom-right':
        return { ...base, bottom: 16, right: 16 };
      default:
        return { ...base, bottom: 16, right: 16 };
    }
  };
  
  // Only apply animation class during enter/exit, not when stable
  const getAnimationClass = () => {
    if (exiting) {
      switch (salesNotification.animation) {
        case 'slideUp': return 'animate-slide-out-down';
        case 'zoom': return 'animate-zoom-out';
        case 'bounce': return 'animate-bounce-out';
        default: return 'animate-fade-out';
      }
    }
    if (!hasEntered) {
      switch (salesNotification.animation) {
        case 'slideUp': return 'animate-slide-in-up';
        case 'zoom': return 'animate-zoom-in';
        case 'bounce': return 'animate-bounce-in';
        default: return 'animate-fade-in';
      }
    }
    // Stable state - no animation class
    return '';
  };
  
  // Determine font size class
  const getFontSizeClass = () => {
    switch (salesNotification.fontSize) {
      case 'small': return 'text-xs';
      case 'large': return 'text-base';
      default: return 'text-sm';
    }
  };
  
  // Get padding class
  const getPaddingClass = () => {
    switch (salesNotification.style?.padding || 'medium') {
      case 'small': return 'py-1 px-2';
      case 'large': return 'py-3 px-4';
      default: return 'py-2 px-3';
    }
  };
  
  // Get styles from config with safe defaults
  const style = salesNotification.style || {
    bgColor: '#FFFFFF',
    textColor: '#333333',
    borderColor: '#E1E1E1',
    borderRadius: 4,
    opacity: 100,
    boxShadow: true,
    padding: 'medium'
  };
  const { bgColor, textColor, borderColor, borderRadius = 4, opacity = 100 } = style;
  
  // Get icon size class
  const getIconSizeClass = () => {
    switch (salesNotification.iconSize || 'medium') {
      case 'small': return 'w-4 h-4';
      case 'large': return 'w-8 h-8';
      default: return 'w-6 h-6';
    }
  };

  // ALINHAMENTO VERTICAL ÍCONE x PRIMEIRA LINHA — espelha o runner.js.
  // Com `items-center` o ícone descia para o meio do parágrafo assim que o
  // texto passava a quebrar (medido em Chromium: ~19,8px abaixo do centro da
  // 1ª linha com 3 linhas). O card alinha pelo TOPO e a diferença de altura
  // entre o ícone e a 1ª linha é compensada no item mais baixo dos dois, para
  // o caso de 1 linha continuar renderizando igual ao de antes.
  const ICON_PX = { small: 16, large: 32, medium: 24 } as const;
  const FONT_PX = { small: 12, large: 16, medium: 14 } as const;
  // 1.35, o mesmo do runner.js (SN_LINE_HEIGHT) — o preview tem que quebrar a
  // frase nas mesmas linhas que o player, e `leading-snug` do Tailwind é 1.375.
  const LINE_HEIGHT = 1.35;
  const iconPx = ICON_PX[(salesNotification.iconSize || 'medium') as keyof typeof ICON_PX] ?? ICON_PX.medium;
  const fontPx = FONT_PX[(salesNotification.fontSize || 'medium') as keyof typeof FONT_PX] ?? FONT_PX.medium;
  const linePx = fontPx * LINE_HEIGHT;
  const iconOffsetPx = Math.max(0, (linePx - iconPx) / 2);
  const textOffsetPx = salesNotification.showIcon !== false ? Math.max(0, (iconPx - linePx) / 2) : 0;
  
  const positionStyles = getPositionStyles();
  const animationClass = getAnimationClass();
  
  // Build notification content
  const notificationContent = (
    <>
      {salesNotification.showIcon !== false && (
        <div className="flex-shrink-0 flex items-start" style={{ marginTop: iconOffsetPx }}>
          {getPlatformIcon() ? (
            <div className={getIconSizeClass()}>
              <img 
                src={getPlatformIcon()} 
                alt={PLATFORM_DISPLAY_NAMES[salesNotification.platform as keyof typeof PLATFORM_DISPLAY_NAMES] || translateInterfaceText("Ícone")}
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <svg className={getIconSizeClass()} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke={textColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 4L12 14.01L9 11.01" stroke={textColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
      )}
      
      {/* min-w-0 deixa o flex item encolher e o texto quebrar; sem ele a frase
          estoura o card. `truncate max-w-[280px]` cortava qualquer mensagem um
          pouco mais longa — o player mostra o texto inteiro, e o preview tem
          que mostrar o mesmo. Quem limita a largura é o card. */}
      <div className="flex flex-col min-w-0">
        <div
          className={cn("font-medium leading-[1.35] [overflow-wrap:anywhere]", getFontSizeClass())}
          style={{ paddingTop: textOffsetPx }}
        >
          {currentMessage}
        </div>
      </div>
      
      {salesNotification.soundEnabled && (
        <audio ref={audioRef} className="hidden">
          <source src="/assets/sounds/cash-register.mp3" type="audio/mpeg" />
        </audio>
      )}
    </>
  );
  
  const commonStyles: React.CSSProperties = {
    ...positionStyles,
    backgroundColor: bgColor,
    color: textColor,
    border: borderColor ? `1px solid ${borderColor}` : `1px solid #E1E1E1`,
    borderRadius: `${borderRadius}px`,
    opacity: opacity / 100,
    boxShadow: style?.boxShadow !== false ? '0 4px 18px rgba(0,0,0,0.05)' : 'none',
    // Ensure stable rendering with GPU compositing
    willChange: animationClass ? 'opacity, transform' : 'auto',
  };
  
  // Nunca jogar a URL crua num href: documentos já salvos podem trazer
  // `javascript:` (executaria no clique) ou uma URL sem esquema (viraria
  // navegação relativa). Mesma sanitização das outras duas camadas.
  const safeRedirectUrl = normalizeRedirectUrl(salesNotification.redirectUrl);

  if (safeRedirectUrl) {
    return (
      <a 
        href={safeRedirectUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          'z-50 w-max max-w-[min(85%,420px)] flex items-start gap-3 cursor-pointer',
          animationClass,
          getPaddingClass()
        )}
        style={commonStyles}
      >
        {notificationContent}
      </a>
    );
  }
  
  return (
    <div 
      className={cn(
        'z-50 w-max max-w-[min(85%,420px)] flex items-start gap-3 cursor-default',
        animationClass,
        getPaddingClass()
      )}
      style={commonStyles}
    >
      {notificationContent}
    </div>
  );
};
